// A retake writes NO experience_user row — an open attempt shows up solely as realtime
// user_info.attempt_no > experience_user.attempt_no, until the next submit writes a row.

import { ATTEMPTS_NO_MAX, ATTEMPTS_NO_MIN } from './attemptsCap';

/* eslint-disable @typescript-eslint/no-explicit-any */

/** Org flag + assessment switch — the pair every attempts gate reads. */
export const multipleAttemptsAvailable = (experience: any, enabledFeatures: any): boolean =>
  Boolean(enabledFeatures?.multiple_attempts && experience?.settings?.multiple_attempts_enabled);

/** Taken attempts so far — attempts list length; a payload without the list
 *  (feature off / older BE) counts the single taken EU. */
export const attemptsTakenCount = (experience: any): number =>
  experience?.experience_user_attempts?.length ?? (experience?.experience_user ? 1 : 0);

/** Attempts left under the cap. Cap must be a valid in-range integer (no
 *  unlimited) — anything else means zero retakes. Deliberately NOT attemptsCap:
 *  the deliver form shows a legacy 1 as the minimum, but here a legacy row must
 *  grant no retakes at all. */
export const attemptsRemaining = (experience: any): number => {
  const cap = Number(experience?.settings?.attempts_no);
  if (!Number.isInteger(cap) || cap < ATTEMPTS_NO_MIN) return 0;
  return Math.max(0, Math.min(cap, ATTEMPTS_NO_MAX) - attemptsTakenCount(experience));
};

/**
 * An attempt past the latest submitted one is open — the student is mid-retake.
 * Reads the realtime attempt number because no payload field reports it.
 */
export const isRetakeOpen = (
  experience: any,
  enabledFeatures: any,
  realtimeAttemptNo: unknown
): boolean => {
  if (!multipleAttemptsAvailable(experience, enabledFeatures)) return false;
  const latestTakenEu = experience?.experience_user;
  if (latestTakenEu?.status !== 'taken') return false;
  if (attemptsRemaining(experience) < 1) return false;
  const latestTakenAttemptNo = Number(latestTakenEu.attempt_no);
  // Attempts start at 1, so 0 (what null/'' coerce to) is not a baseline.
  if (!Number.isInteger(latestTakenAttemptNo) || latestTakenAttemptNo < 1) return false;
  return Number(realtimeAttemptNo) > latestTakenAttemptNo;
};

/** Retake button gate: org flag + switch + quick_check + digital + open +
 *  no attempt already open + no active peer review + attempts left. */
export const canRetakeExperience = (
  experience: any,
  enabledFeatures: any,
  realtimeAttemptNo: unknown
): boolean => {
  const settings = experience?.settings ?? {};
  if (!multipleAttemptsAvailable(experience, enabledFeatures)) return false;
  if (settings.assessment_type !== 'quick_check') return false;
  if (settings.mode === 'paper') return false;
  if (settings.peer_review_enabled) return false;
  if (experience?.status === 'closed') return false;
  if (isRetakeOpen(experience, enabledFeatures, realtimeAttemptNo)) return false;
  if (!experience?.experience_user) return false;
  return attemptsRemaining(experience) > 0;
};

/* eslint-enable @typescript-eslint/no-explicit-any */
